/**
 * FP-011 (2026-09-09 audit remediation) — Forgot Password entry page.
 *
 * The backend has had a working, rate-limited public
 * `POST /auth/forgot-password` endpoint, but Login.jsx never linked to
 * anywhere a user could reach it — the reset flow was undiscoverable
 * even after the completion page (ResetPassword.jsx) existed. Always
 * shows the same generic confirmation regardless of whether the email
 * matched an account, matching the backend's anti-enumeration design.
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, ArrowLeft } from "lucide-react";
import LegalFooter from "@/components/legal/LegalFooter";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (e) {
      // 429 (rate-limited) is the only expected non-generic error —
      // everything else the backend already normalizes to 200.
      setError(e?.response?.data?.detail || "Something went wrong. Try again shortly.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--fop-bg)] flex flex-col">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm rounded-xl border border-[var(--fop-border)] bg-[var(--fop-surface)] p-8">
          {sent ? (
            <div data-testid="forgot-password-sent" className="text-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
              <div className="text-base font-medium text-[var(--fop-text)]">Check your email</div>
              <div className="text-sm text-[var(--fop-text-2)] mt-1">
                If an account exists for <span className="font-mono">{email}</span>, a reset link is on its way.
              </div>
              <Link to="/login" data-testid="forgot-password-back-to-login" className="inline-block mt-4 px-4 py-2 bg-[var(--fop-primary)] text-black rounded text-sm font-mono uppercase tracking-wider hover:opacity-90">
                Back to sign in
              </Link>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-[0.18em] text-[var(--fop-text-3)] mb-1">
                ForgedOps Plans
              </div>
              <h1 className="text-2xl font-semibold text-[var(--fop-text)] mb-1">Forgot password</h1>
              <div className="text-sm text-[var(--fop-text-2)] mb-6">
                Enter your account email and we'll send a reset link.
              </div>
              <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div>
                  <Label htmlFor="fp-email" className="text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Email</Label>
                  <Input id="fp-email" type="email" required value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          data-testid="forgot-password-email"
                          className="mt-1 bg-[var(--fop-surface-2)] border-[var(--fop-border)]" />
                </div>
                {error && (
                  <div data-testid="forgot-password-error" className="text-xs text-red-400">{error}</div>
                )}
                <Button type="submit" disabled={submitting}
                         data-testid="forgot-password-submit"
                         className="w-full bg-[var(--fop-primary)] hover:opacity-90">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send Reset Link"}
                </Button>
                <Link to="/login" data-testid="forgot-password-cancel" className="flex items-center justify-center gap-1 text-xs text-[var(--fop-text-3)] hover:text-[var(--fop-text)] mt-2">
                  <ArrowLeft size={11} /> Back to sign in
                </Link>
              </form>
            </>
          )}
        </div>
      </div>
      <LegalFooter />
    </div>
  );
}
